import {FC, useState, useMemo} from "react";
import {Box, Chip, styled} from "@mui/material";
import {AutoAwesomeOutlined, CalendarTodayOutlined, CheckCircleOutline as Check, LockOpenOutlined, LockOutlined, PersonOutlined, SupervisorAccountOutlined} from "@mui/icons-material";
import {
	FeedItemDto,
	FeedItemDtoFeedTier,
	useGetUserFeed
} from "@/app2/shared/data";
import {
	formatCalendarYmdForLocale,
	formatDueInstantInTimezone,
	isDueCalendarOverdue
} from "@/app2/shared/utils/dateManipulation";
import {BulkCompleteModal} from "../components/BulkCompleteModal";
import {CustomToolbar} from "@/components/admin/bulkEnrollment/CustomToolbar";
import useDevCycleSdk from "@/hooks/useDevCycleSdk";
import {DataGridPro, GridColDef, GridRowParams, GridSelectionModel} from "@mui/x-data-grid-pro";

interface UserTrainingFeedTabProps {
	userId: number;
	archived?: boolean;
}

/** Feed item plus stable row id for DataGrid */
type FeedGridRow = FeedItemDto & { rowId: string };

/** Course shape returned in feed items (gen uses Record<string, unknown>) */
interface FeedItemCourse {
	title?: string;
	slug?: string;
}

/**
 * Admin view of a user's training feed
 *
 * Shows all pending lessons/quizzes with bulk complete action.
 * Uses new improve-api endpoints.
 */
export const UserTrainingFeedTab: FC<UserTrainingFeedTabProps> = ({userId, archived}) => {
	const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
	const {data, isLoading, refetch} = useGetUserFeed(userId, {timezone});
	const [selectedIds, setSelectedIds] = useState<GridSelectionModel>([]);
	const [bulkCompleteOpen, setBulkCompleteOpen] = useState(false);
	const {archiveUserFlow} = useDevCycleSdk();

	// Map feed items to rows with unique IDs
	const rows = useMemo((): FeedGridRow[] => {
		if (!data?.data) return [];
		return data.data.map((item) => ({
			...item,
			rowId: `${item.id}_${item.type}_${(item.course as FeedItemCourse)?.slug ?? "unknown"}`
		}));
	}, [data]);

	// Only allow selection of incomplete items
	const isRowSelectable = (params: GridRowParams<FeedGridRow>) => !params.row.isCompleted;

	// Get selected enrollment IDs for bulk complete (only incomplete items)
	const selectedEnrollmentIds = useMemo(() => {
		return rows
			.filter((row) => selectedIds.includes(row.rowId) && !row.isCompleted)
			.map((row) => row.enrollmentId);
	}, [rows, selectedIds]);

	const columns: GridColDef<FeedGridRow>[] = [
		{
			field: "title",
			headerName: "Lesson",
			flex: 1,
			minWidth: 240
		},
		{
			field: "isCompleted",
			headerName: "Status",
			width: 120,
			renderCell: (params) => {
				if (params.row.tier === "past_due") {
					return <Chip color="error" label="Overdue" size="small" />
				}

				return params.value ? (
					<Chip color="success" label="Completed" size="small" />
				) : (
					<Chip color="default" variant="outlined" label="Upcoming" size="small" />
				)
			}
		},
		{
			field: "feedTier",
			headerName: "Enrollment Type",
			flex: 1,
			renderCell: (params) => {
				const tier = params.row.feedTier;
				switch (tier) {
					case FeedItemDtoFeedTier["due-date"]:
						return <CustomChip color="info" label="Due Date Assigned" size="small" icon={<CalendarTodayOutlined />} />;
					case FeedItemDtoFeedTier["admin"]:
						return <CustomChip color="primary" label="Priority Assigned" size="small" icon={<SupervisorAccountOutlined />} />;
					case FeedItemDtoFeedTier["self"]:
						return <CustomChip label="Self-Enrolled" size="small" icon={<PersonOutlined />} sx={{backgroundColor: "#333333"}} />;
					case FeedItemDtoFeedTier["auto"]:
						return <CustomChip color="warning" label="Auto-Enrolled" size="small" icon={<AutoAwesomeOutlined />} />;
					default:
						return <CustomChip color="default" label="Unknown" size="small" />;
				}
			}
		},
		{
			field: "dueDate",
			headerName: "Due Date",
			flex: 1,
			renderCell: (params) => {
				const row = params.row;
				const cal = row.dueDateCalendar;
				if (cal) {
					const isOverdue = row.tier === "past_due";
					return (
						<span style={{color: isOverdue ? "#d32f2f" : "inherit"}}>
							{formatCalendarYmdForLocale(cal)}
						</span>
					);
				}
				const value = params.value;
				if (value == null) return "-";
				const dueInstant = new Date(value as string | number | Date);
				if (Number.isNaN(dueInstant.getTime())) return "-";
				const isOverdue = isDueCalendarOverdue(dueInstant, timezone);
				return (
					<span style={{color: isOverdue ? "#d32f2f" : "inherit"}}>
						{formatDueInstantInTimezone(dueInstant, timezone)}
					</span>
				);
			}
		},
		{
			field: "course",
			headerName: "Parent Course",
			flex: 1,
			valueGetter: (params) => (params.row.course as FeedItemCourse)?.title ?? "-"
		},
		{
			field: "isLocked",
			headerName: "Course Progression",
			width: 160,
			renderCell: (params) =>
				params.value ?
					<CustomChip color="warning" label="Linear" size="small" variant="outlined" icon={<LockOutlined />} /> :
					<CustomChip color="default" label="Free Path" size="small" variant="outlined" icon={<LockOpenOutlined />} />
		}
	];

	const handleBulkComplete = () => {
		if (selectedEnrollmentIds.length > 0) {
			setBulkCompleteOpen(true);
		}
	};

	const handleBulkCompleteSuccess = () => {
		setSelectedIds([]);
		setBulkCompleteOpen(false);
		refetch();
	};

	return (
		<Box>
			{/* Data Grid */}
			<DataGridPro<FeedGridRow>
				rows={rows}
				columns={columns}
				loading={isLoading}
				getRowId={(row) => row.rowId}
				checkboxSelection
				disableSelectionOnClick
				isRowSelectable={isRowSelectable}
				selectionModel={selectedIds}
				onSelectionModelChange={setSelectedIds}
				rowsPerPageOptions={[25, 50, 100, 200]}
				components={{Toolbar: (): JSX.Element => (<CustomToolbar>
					{(!archived || !archiveUserFlow) && <Box onClick={handleBulkComplete}><Check /> MARK AS COMPLETED</Box>}
				</CustomToolbar>)
				}}
				initialState={{
					pagination: {pageSize: 25}
				}}
				autoHeight
				sx={{
					"& .MuiDataGrid-row": {cursor: "default"}
				}}
			/>

			{/* Bulk Complete Modal */}
			<BulkCompleteModal
				open={bulkCompleteOpen}
				onClose={() => setBulkCompleteOpen(false)}
				onSuccess={handleBulkCompleteSuccess}
				userId={userId}
				lessonEnrollmentIds={selectedEnrollmentIds}
			/>
		</Box>
	);
};

const CustomChip = styled(Chip)({
	padding: "14px 8px"
});